import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { Section, ACCENT, formatMXN } from "../cuentas/shared";
import { gastoPorProveedor, SIN_PROVEEDOR } from "../../utils/reportes";
import { isInRange, formatCalendarDate } from "../../utils/dates";
import {
  adquisicionDe,
  ingresosPorItem,
  recuperacionItem,
} from "../../utils/catalogo";
import { COSTO_COLOR } from "./shared";
import {
  BriefcaseIcon,
  RectangleStackIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";

// A quién le pagas y qué te está rindiendo: el gasto por proveedor del
// período y la recuperación de la inversión de los servicios que compraste.
const ReporteProveedores = ({ rango }) => {
  const navigate = useNavigate();
  const { costos, conceptos, events, calendarTimezone: tz } = useVenue();

  const proveedoresTop = useMemo(
    () => gastoPorProveedor({ costos, desde: rango.from, hasta: rango.to, timeZone: tz, limite: 15 }),
    [costos, rango, tz]
  );
  const maxGasto = proveedoresTop[0]?.total || 0;
  const totalGasto = proveedoresTop.reduce((s, p) => s + p.total, 0);

  // Fila expandida: los costos del período que componen el total del
  // proveedor — mismo patrón que el Estado de resultados.
  const [provAbierto, setProvAbierto] = useState(null);
  const detalleProv = useMemo(() => {
    if (!provAbierto) return [];
    const t = (v) => (v?.toDate ? v.toDate() : new Date(v || 0));
    const nombreEvento = {};
    (events || []).forEach((e) => {
      if (e?.id) nombreEvento[e.id] = e.nombre || "Sin nombre";
    });
    return (costos || [])
      .filter(
        (c) =>
          (provAbierto === SIN_PROVEEDOR
            ? !c?.proveedorId
            : c?.proveedorId === provAbierto) &&
          isInRange(c.fecha, rango.from, rango.to, tz)
      )
      .map((c) => ({ ...c, eventNombre: c.eventId ? nombreEvento[c.eventId] : null }))
      .sort((a, b) => t(b.fecha) - t(a.fecha));
  }, [provAbierto, costos, events, rango, tz]);

  // Snapshot de siempre (no del período): la pregunta es "¿ya se pagó
  // solo?", no "¿cuánto recuperó este mes?".
  const recuperaciones = useMemo(
    () =>
      (conceptos || [])
        .filter((c) => !c.archivedAt && Number(adquisicionDe(c)?.monto) > 0)
        .map((c) => ({
          item: c,
          rec: recuperacionItem({
            item: c,
            ingresos: ingresosPorItem({ events, itemId: c.id, conceptos }),
            costos,
          }),
        }))
        .filter((x) => x.rec)
        .sort((a, b) => b.rec.pct - a.rec.pct),
    [conceptos, events, costos]
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      <Section icon={BriefcaseIcon} title="Gasto por proveedor">
        {proveedoresTop.length === 0 ? (
          <p className="text-sm text-zinc-400">
            Sin gasto registrado en este período.
          </p>
        ) : (
          <>
            <div className="mb-3 text-sm text-zinc-500">
              Total del período:{" "}
              <span className="font-semibold text-zinc-900 dark:text-white">
                {formatMXN(totalGasto)}
              </span>
            </div>
            <div className="space-y-2.5">
              {proveedoresTop.map((p) => {
                const abierto = provAbierto === p.proveedorId;
                return (
                  <div key={p.proveedorId}>
                    <div
                      onClick={() => setProvAbierto(abierto ? null : p.proveedorId)}
                      className="flex items-center justify-between text-sm cursor-pointer group"
                      title="Ver los costos que lo componen"
                    >
                      <span className="inline-flex items-center gap-1 min-w-0">
                        <ChevronRightIcon
                          className={`h-4 w-4 shrink-0 text-zinc-300 group-hover:text-zinc-500 transition-transform ${
                            abierto ? "rotate-90" : ""
                          }`}
                        />
                        {p.proveedorId === SIN_PROVEEDOR ? (
                          <span className="truncate text-zinc-500 dark:text-zinc-400 italic">
                            {p.nombre}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/proveedores/${p.proveedorId}`);
                            }}
                            className="hover:underline truncate"
                            style={{ color: ACCENT }}
                          >
                            {p.nombre}
                          </button>
                        )}
                      </span>
                      <span className="font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                        {formatMXN(p.total)}
                        <span className="ml-1.5 text-xs font-normal text-zinc-400">
                          {p.eventos > 0
                            ? `${p.eventos} ${p.eventos === 1 ? "evento" : "eventos"}${
                                p.sueltos > 0
                                  ? ` · ${p.sueltos} ${p.sueltos === 1 ? "gasto suelto" : "gastos sueltos"}`
                                  : ""
                              }`
                            : `${p.count} ${p.count === 1 ? "gasto" : "gastos"}`}
                        </span>
                      </span>
                    </div>
                    <div className="mt-0.5 h-1.5 w-full rounded-full bg-zinc-100 dark:bg-zinc-700/60 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${maxGasto > 0 ? Math.round((p.total / maxGasto) * 100) : 0}%`,
                          backgroundColor:
                            p.proveedorId === SIN_PROVEEDOR ? "#A1A1AA" : COSTO_COLOR,
                          opacity: 0.75,
                        }}
                      />
                    </div>
                    {abierto && (
                      <div className="mt-2 mb-1 ml-5 space-y-1">
                        {detalleProv.map((c) => (
                          <div key={c.id} className="flex items-baseline gap-2 text-xs">
                            <span className="text-zinc-400 w-16 shrink-0">
                              {formatCalendarDate(c.fecha, tz, { style: "short" })}
                            </span>
                            {/* Concepto primero y evento después, en un
                                solo renglón: el concepto es lo que distingue
                                un costo de otro (seis costos de una misma
                                boda daban seis veces el nombre de la boda),
                                pero sin el evento no se sabe de dónde salió
                                el gasto. Todo el texto es el link al evento;
                                si no cabe, el `title` lo dice completo. */}
                            {c.eventId ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/dashboard/eventos/${c.eventId}`)}
                                className="min-w-0 flex-1 truncate text-left hover:underline"
                                title={`${c.concepto || "Costo"}${
                                  c.eventNombre ? ` · ${c.eventNombre}` : ""
                                }`}
                              >
                                <span style={{ color: ACCENT }}>
                                  {c.concepto || "Costo"}
                                </span>
                                {c.eventNombre && (
                                  <span className="text-zinc-400"> · {c.eventNombre}</span>
                                )}
                              </button>
                            ) : (
                              <span className="min-w-0 flex-1 truncate text-zinc-500">
                                {c.concepto || "Gasto suelto"}
                              </span>
                            )}
                            {c.estado === "por_pagar" && (
                              <span className="text-amber-600 whitespace-nowrap">por pagar</span>
                            )}
                            <span className="ml-auto font-medium text-red-600 whitespace-nowrap">
                              {formatMXN(c.monto)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-zinc-400">
              El proveedor se pega solo cuando el costo nace de un servicio de
              tu catálogo o de una comisión de planner; lo que capturas a mano
              cae en «Sin proveedor asignado». El margen que te deja cada
              proveedor por evento vive en el tab Balance del evento.
            </p>
          </>
        )}
      </Section>

      <Section icon={RectangleStackIcon} title="Recuperación de inversiones">
        {recuperaciones.length === 0 ? (
          <p className="text-sm text-zinc-400">
            Registra el costo de adquisición de lo que compraste (en Servicios)
            para ver cuándo se paga solo.
          </p>
        ) : (
          <div className="space-y-3">
            {recuperaciones.map(({ item, rec }) => (
              <div key={item.id}>
                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/servicios/${item.id}`)}
                    className="hover:underline truncate"
                    style={{ color: ACCENT }}
                  >
                    {item.nombre}
                  </button>
                  <span
                    className={`text-xs font-semibold whitespace-nowrap ${
                      rec.pagadoEn ? "text-emerald-600" : "text-zinc-500"
                    }`}
                  >
                    {rec.pagadoEn
                      ? rec.meses != null
                        ? `Recuperada en ${rec.meses} ${rec.meses === 1 ? "mes" : "meses"}`
                        : "Recuperada"
                      : `${formatMXN(rec.recuperado)} de ${formatMXN(rec.inversion)}`}
                  </span>
                </div>
                <div className="mt-0.5 h-1.5 w-full rounded-full bg-zinc-100 dark:bg-zinc-700/60 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${rec.pagadoEn ? "bg-emerald-500" : "bg-amber-500"}`}
                    style={{ width: `${Math.round(rec.pct * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-4 text-xs text-zinc-400">
          Es un acumulado desde la compra, no del período: la pregunta es si
          ya se pagó solo.
        </p>
      </Section>
    </div>
  );
};

export default ReporteProveedores;
